<template>
  <modal
    :show.sync="showPriceAdjusments"
    body-classes
    @close="hideModalFromButtonClose()"
    no-close-on-backdrop
  >
    <template slot="header">
      <h5 class="subtitle" id="modalPriceAdjusments">
        Price Adjustments
      </h5>
    </template>

    <div class="row align-items-center mt-4 forms">
      <div class="col-12">
        <div class="rows-extra" id="rows-extra">
          <div class="row">
            <div class="col-xl-12">
              <WSelect
                v-model="$v.reason.$model"
                label="Reason for adjustment"
                placeholder=""
                filterable
                clearable
                :error="$v.reason.$error ? 'Select an reason' : ''"
                markrequired
              >
                <el-option
                  v-for="(item, index) in reasons"
                  :value="item.name"
                  :label="item.name"
                  :key="index"
                >
                  <span class="d-block label-fs2 strong">{{ item.name }}</span>
                  <span class="d-block" style="color: #8492a6">{{
                    item.description
                  }}</span>
                </el-option>
              </WSelect>
            </div>

            <div
              class="col-xl-6 col-lg-6 col-md-6 col-sm-6 col-12 align-self-center"
            >
              <BaseNumberInput
                v-model.number="$v.netPrice.$model"
                label="Net Price"
                placeholder="Net Price"
                class="input-append with-prepend"
                @input="getGroosPrice"
                :error="$v.netPrice.$error ? `Enter a Price` : ''"
                markrequired
              >
                <template slot="prepend">{{ currency.symbol }}</template>
                <template slot="append">{{ currency.code }}</template>
              </BaseNumberInput>
            </div>

            <div
              class="col-xl-6 col-lg-6 col-md-6 col-sm-6 col-12 align-self-center"
            >
              <BaseNumberInput
                v-model.number="$v.grossPrice.$model"
                label="Gross Price"
                placeholder="Gross Price"
                class="input-append with-prepend"
                :error="$v.grossPrice.$error ? `Enter a Price` : ''"
                markrequired
              >
                <template slot="prepend">{{ currency.symbol }}</template>
                <template slot="append">{{ currency.code }}</template>
              </BaseNumberInput>
            </div>
          </div>
        </div>
      </div>
    </div>

    <template slot="footer">
      <base-button
        type="outline-neutral "
        class="min-button mb-0"
        @click="hideModalFromButtonCancel()"
        >Cancel</base-button
      >
      <base-button
        type="primary"
        class="min-button ml-auto mb-0"
        @click="updatePricing()"
        >Accept</base-button
      >
    </template>
  </modal>
</template>

<script>
// @ is an alias to /src
import WSelect from "@/components/Select";
import {
  required,
  minLength,
  maxLength,
  decimal,
  minValue
} from "vuelidate/lib/validators";
import Swal from "sweetalert2";
WSelect.computed.readonly = function() {
  const isIE = !this.$isServer && !Number.isNaN(Number(document.documentMode));
  return (
    !this.filterable ||
    this.multiple ||
    (!isIE && !this.visible && !this.remote)
  );
};

export default {
  name: "PriceAdjustments",
  components: {
    WSelect
  },
  props: {
    show: {
      type: Boolean,
      required: true
    },
    markUp: {
      type: Number,
      required: true
    },
    markUpType: {
      type: String,
      required: true
    },
    lastGrossPrice: {
      type: Number,
      required: true
    },
    lastNetPrice: {
      type: Number,
      required: true
    },
    currency: {
      code: {
        type: String,
        default: "EUR",
        description: ""
      },
      symbol: {
        type: String,
        default: "€",
        description: ""
      }
    }
  },
  watch: {
    show: {
      handler: function(current, old) {
        this.showPriceAdjusments = current;
      },
      deep: true
    },
    lastGrossPrice: {
      handler: function(current, old) {
        this.grossPrice = current;
      },
      deep: true
    },
    lastNetPrice: {
      handler: function(current, old) {
        this.netPrice = current;
      },
      deep: true
    },
    markUpType: {
      handler: function(current, old) {
        this.localMarkUpType = current;
      },
      deep: true
    },
    markUp: {
      handler: function(current, old) {
        this.localMarkUp = current;
      },
      deep: true
    }
  },
  data() {
    return {
      showPriceAdjusments: false,
      reasons: [
        {
          name: "Distance Miscalculation",
          description:
            "The current rate (RH) was quoted too high due to a miscalculation of the journey distance."
        },
        {
          name: "Matching Competitor Price",
          description:
            " Adjusting the amount up or down to match the competitor's price."
        },
        {
          name: "Outdated Rate Quotation",
          description:
            "The current rate (RH) was quoted too low due to an outdated tariff."
        },
        {
          name: "Profit Opportunity",
          description:
            "The rate was increased due to the client being particularly demanding."
        },
        {
          name: "Rounding Adjustments",
          description: "The amount was rounded up or down."
        },
        {
          name: "Special Hourly Rates",
          description:
            "The current rate (RH) was quoted too high on an hourly basis, but the supplier offers a special rate for 8 hours, which is different from the per-hour rate multiplied by 8."
        },
        {
          name: "Supplier Error Compensation",
          description:
            "There was an error from the supplier, and they are providing compensation with a discount."
        }
      ],
      localMarkUpType: "",
      localMarkUp: 0,
      grossPrice: "",
      netPrice: "",
      reason: ""
    };
  },
  async created() {
    this.localMarkUpType = this.markUpType;
    this.localMarkUp = this.markUp;
    this.grossPrice = this.lastGrossPrice;
    this.netPrice = this.lastNetPrice;
  },
  methods: {
    updatePricing() {
      let isEqual =
        this.grossPrice === this.lastGrossPrice &&
        this.netPrice === this.lastNetPrice;
      if (!isEqual) {
        if (!this.$v.$invalid) {
          let netProfit = parseFloat(
            (this.grossPrice - this.netPrice).toFixed(2)
          );
          this.$emit("change", {
            grossPrice: this.grossPrice,
            netPrice: this.netPrice,
            netProfit: netProfit,
            reason: this.reason
          });
          this.formatData();
        } else {
          this.$v.$touch();
          this.$notify({
            type: "warning",
            title: "Important!",
            message: "Please complete the required attributes",
            icon: "flaticon-warning-1"
          });
        }
      } else {
        this.closePriceAdjustments();
      }
    },
    closePriceAdjustments() {
      this.showPriceAdjusments = false;
      this.$emit("close", false);
    },
    async hideModalFromButtonClose() {
      this.showPriceAdjusments = true;
      await this.detectChanges();
    },
    async hideModalFromButtonCancel() {
      await this.detectChanges();
    },
    async detectChanges() {
      let isEqual =
        this.grossPrice === this.lastGrossPrice &&
        this.netPrice === this.lastNetPrice;
      if (!isEqual) {
        let { value } = await Swal.fire({
          title: "Important",
          html:
            "The Extras has been modified. <br> Do you want to save changes?",
          imageUrl: require("rhomtrip_assets/src/img/icons/alerts/warning.png"),
          buttonsStyling: false,
          showCloseButton: true,
          showCancelButton: true,
          focusConfirm: false,
          allowOutsideClick: false,
          reverseButtons: true,
          cancelButtonText: "No",

          confirmButtonText: "Yes",
          confirmButtonClass: "btn btn-primary btn-small-one"
        });
        if (value) {
          this.updatePricing();
        } else {
          this.closePriceAdjustments();
          this.formatData();
        }
      } else {
        this.formatData();
        this.closePriceAdjustments();
      }
    },
    getGroosPrice(value) {
      let netPrice = parseFloat(value);
      let grossPrice =
        this.localMarkUpType === "markup"
          ? netPrice / (1 - this.localMarkUp / 100)
          : netPrice * (1 - this.localMarkUp / 100);
      this.grossPrice = parseFloat(grossPrice.toFixed(2));
    },
    formatData() {
      this.reason = "";
      this.$v.$reset();
    }
  },
  validations: {
    reason: {
      required
    },
    netPrice: {
      required,
      decimal,
      minValue: minValue(1)
    },
    grossPrice: {
      required,
      decimal,
      minValue: minValue(1)
    }
  }
};
</script>
